// domain/constants/modifier.js

/**
 * Modifiers offered as suggestions when authoring a payer's BASE modifier set.
 *
 * SUGGESTIONS, NEVER A CLOSED SET. Modifiers come from two systems — CPT Level I (two digits, AMA) and
 * HCPCS Level II (alphanumeric, CMS) — which together run into the hundreds, and each payer decides
 * which it requires. State Medicaid programs go further and define what some of them MEAN. So the
 * control accepts anything typed; this list only saves somebody from retyping the common ones and
 * from a silent typo, which on a modifier is a denial.
 *
 * ONLY MODIFIERS THAT ARE CONSTANT FOR A (PAYER × SERVICE) BELONG HERE, because that is what this
 * field is: an unconditional set applied to every line for that payer and that service. Three whole
 * families are therefore left out, and each is left out for its own reason rather than for tidiness:
 *
 *   - THE CREDENTIAL TIER (HM, HN, HO, HP). These vary with who delivered the service, so they are
 *     authored as CONDITIONAL rules against the performer rather than as a flat set — see
 *     `CREDENTIAL_MODIFIER_SUGGESTIONS` below, which is the list this one sends them to. Suggesting
 *     them here invites setting HO flat on every 97155 — the overbilling direction, which pays,
 *     sails a year, and returns as recoupment. A clinic that typed one here as a stopgap before the
 *     conditional rules existed keeps working: the resolver unions the two sets and de-duplicates,
 *     so the stopgap does not double the modifier once a rule starts producing it.
 *   - TELEHEALTH (95, GT, 93). The payer's telehealth modifier is its own field on the funding source,
 *     because it describes the modality rather than the service. Offering it here would be two ways to
 *     say one thing. Audio-only (93) is separately deferred — behavior cannot be observed without
 *     video, so audio-only ABA is rare to nonexistent.
 *   - CIRCUMSTANCE MODIFIERS (59, XE, XP, XS, XU, 52, UN–US). These describe what happened in a
 *     particular encounter — services that would otherwise bundle, a session cut short, how many
 *     clients were in the room. The modifier brief put them out of scope as a different mechanism:
 *     they attach to an encounter, not to a payer's standing rule. Authoring 59 as a constant would
 *     put it on every line, which is an audit magnet.
 *
 * NOTHING HERE IS CPT, and that falls out of the filter rather than being arranged: every modifier
 * this field should carry is HCPCS Level II, which CMS publishes free. So unlike the service code
 * catalog, this list needs no paraphrasing to avoid shipping AMA-licensed descriptor text.
 *
 * A CLAIM LINE CARRIES AT MOST FOUR, which the control enforces rather than leaving to the payer to
 * reject. Order can matter — payers often want the credential modifier first — so the authored order
 * is preserved rather than sorted. That rule bites once the credential axis is resolved; until then
 * there is nothing whose position is prescribed.
 *
 * WHAT A MODIFIER MEANS IS TRANSLATED, so the caption is a KEY rather than words. The codes
 * themselves are not: HCPCS is one published vocabulary and a payer rejects anything but the literal
 * two characters, so `value` doubles as the label in every language. That split is why this stays a
 * domain constant — which modifiers to offer, and which families to withhold, are facts about
 * billing; what each one READS as belongs to a presenter, which is also the only layer allowed to
 * reach `boot/i18n`.
 *
 * @typedef {{ value: string, captionKey: string }} ModifierSuggestion
 */

/**
 * The U-series carries no meaning we can state.
 *
 * Every one of these is "Medicaid level of care, as each state defines it", so a caption naming a
 * level would assert something true in one state and wrong in the next. Saying that it is
 * state-defined is the only honest label, and it is still worth offering: state Medicaid U-modifiers
 * are the case this whole field exists for, and the value of the list here is catching `U03` typed
 * for `U3` rather than explaining anything.
 */
const STATE_DEFINED = ['U1', 'U2', 'U3', 'U4', 'U5', 'U6', 'U7', 'U8', 'U9', 'UA', 'UB', 'UC', 'UD'];

/** One caption for the whole U-series: naming a level would assert something state-specific. */
const STATE_DEFINED_CAPTION_KEY = 'stateDefined';

/** @type {ModifierSuggestion[]} */
export const MODIFIER_SUGGESTIONS = [
  { value: 'HQ', captionKey: 'HQ' },
  { value: 'HA', captionKey: 'HA' },
  { value: 'TF', captionKey: 'TF' },
  { value: 'TG', captionKey: 'TG' },
  ...STATE_DEFINED.map((code) => ({ value: code, captionKey: STATE_DEFINED_CAPTION_KEY })),
];

/**
 * Modifiers offered when authoring a CONDITIONAL rule — what a tier earns the line.
 *
 * SEPARATE FROM THE BASE LIST ABOVE, which is the point of having two. These four are exactly the
 * family that list excludes, because a modifier that varies with the performer must not be authorable
 * as a constant; offering them in both places would undo that in one click.
 *
 * THE CAPTIONS ARE THE HCPCS DEFINITIONS, WHICH ARE ABOUT EDUCATION, and that mismatch is worth
 * seeing while authoring rather than discovering in a denial. CMS defines these four by degree level;
 * payers borrowed them as CREDENTIAL tiers, so a sheet routinely says "HO = master's-level BCBA" and
 * means the certification at least as much as the degree. A caption asserting the payer's reading
 * would be wrong for the payers that kept the literal one, so it states what CMS published and leaves
 * the payer's own wording to the rule the clinic is writing.
 *
 * NOT A CLOSED SET EITHER. State Medicaid programs express credential tiers with their own
 * U-modifiers, so the control accepts anything typed, exactly as the base one does.
 *
 * @type {ModifierSuggestion[]}
 */
export const CREDENTIAL_MODIFIER_SUGGESTIONS = [
  { value: 'HM', captionKey: 'HM' },
  { value: 'HN', captionKey: 'HN' },
  { value: 'HO', captionKey: 'HO' },
  { value: 'HP', captionKey: 'HP' },
];

/** The most a claim line carries. A fifth has nowhere to go and the payer rejects the line. */
export const MAX_MODIFIERS_PER_LINE = 4;
